import React, { useState, useMemo } from 'react';
import { Box, Skeleton, Typography, Pagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SmartGroupingSystem from './SmartGroupingSystem';
import SelectableAlertCard from '../shared/SelectableAlertCard';
import AlertCardCollectionProvider from '../shared/AlertCardCollectionProvider';
import { theme, ThemedTypography } from '../../themes/technicalAlerts';
import useTechnicalAlertsGroups from '../../hooks/useTechnicalAlertsGroups';
import { useAuth } from '../../context/AuthContext';

// Loading skeleton for cards
const CardSkeleton = () => (
  <Box sx={{
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    p: 2
  }}>
    <Skeleton variant="rectangular" width="100%" height={20} sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" width="80%" height={16} sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" width="60%" height={14} sx={{ mb: 2 }} />
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
      <Skeleton variant="rectangular" height={40} />
      <Skeleton variant="rectangular" height={40} />
      <Skeleton variant="rectangular" height={40} />
      <Skeleton variant="rectangular" height={40} />
    </Box>
  </Box>
);

// Empty state component
const EmptyState = () => {
  const { t } = useTranslation();
  
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      px: 4,
      textAlign: 'center'
    }}>
      <Box sx={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: theme.colors.background.light,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3
      }}>
        <Typography sx={{
          fontSize: '32px',
          color: theme.colors.text.muted
        }}>
          📋
        </Typography>
      </Box>
      
      <ThemedTypography.H2 sx={{ mb: 1, color: theme.colors.text.secondary }}>
        {t('noAlertsFound')}
      </ThemedTypography.H2>
      
      <ThemedTypography.Body1 sx={{ color: theme.colors.text.muted }}>
        {t('allSystemsRunning')}
      </ThemedTypography.Body1>
    </Box>
  );
};

// Main cards view component - Always uses smart grouping with backend-aggregated data
const AlertCardsView = ({ 
  filters = {},  // Add filters prop to fetch grouped data
  loading: externalLoading = false,
  selectedRowKeys = [],
  onSelectionChange,
  expandedCards = {},
  onCardExpand,
  expandedGroups = {}, // Centralized expanded groups state
  onGroupToggle, // Group toggle handler from parent
  groupingStrategy = 'store_cluster', // Always grouped, only strategy changes
  onRefetch,
  onPaginationChange, // Callback to send pagination data to parent
  // Alert action functions from parent
  actionLoading,
  confirmBroken,
  confirmNotBroken,
  showToast // Add showToast prop
}) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  
  const [localExpandedCards, setLocalExpandedCards] = useState(expandedCards);
  const [pageSize] = useState(10);
  // Fetch pre-grouped data from backend with lazy loading and pagination
  const { 
    groups, 
    metadata, 
    pagination, 
    loading: groupsLoading, 
    error, 
    refetch,
    expandedGroups: hookExpandedGroups,
    toggleGroupExpansion,
    loadMoreGroups,
    canLoadMore,
    preserveState,
    page: currentPage,
    setCurrentPage
  } = useTechnicalAlertsGroups(filters, groupingStrategy, 1, pageSize);
  const loading = externalLoading || groupsLoading;

  // Pass refetch and preserveState functions to parent component
  React.useEffect(() => {
    if (onRefetch && typeof onRefetch === 'function' && refetch && typeof refetch === 'function') {
      onRefetch(refetch, preserveState, metadata);
    }
  }, [onRefetch, refetch, preserveState, metadata]);



  // Handle page change
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  // Reset page when filters or grouping strategy change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters, groupingStrategy, setCurrentPage]);

  // Notify parent about pagination changes
  React.useEffect(() => {
    if (onPaginationChange && pagination) {
      onPaginationChange({
        pagination,
        page: currentPage,
        handlePageChange,
        viewMode: 'cards'
      });
    }
  }, [pagination, currentPage, onPaginationChange]);

  // Handle card expand/collapse
  const handleCardExpand = (cardKey, expanded) => {
    setLocalExpandedCards(prev => ({
      ...prev,
      [cardKey]: expanded
    }));
    
    onCardExpand?.(cardKey, expanded);
  };

  // Error state
  if (error) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 4,
        textAlign: 'center'
      }}>
        <ThemedTypography.H3 sx={{ mb: 1, color: theme.colors.alert }}>
          {t('errorLoadingAlerts')}
        </ThemedTypography.H3>
        <ThemedTypography.Body2 sx={{ color: theme.colors.text.muted }}>
          {error.message || t('pleaseTryAgain')}
        </ThemedTypography.Body2>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3].map(i => (
            <CardSkeleton key={i} />
          ))}
        </Box>
      )}
      
      {/* Empty state */}
      {!loading && (!groups || groups.length === 0) && (
        <EmptyState />
      )}
      
      {/* Groups display */}
      {!loading && groups && groups.length > 0 && (
        <>
          <SmartGroupingSystem
            groups={groups}  // Pass pre-grouped data instead of raw alerts
            metadata={metadata}  // Global metadata across ALL groups
            pagination={pagination}  // Pagination info
            loading={loading}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={onSelectionChange}
            expandedCards={localExpandedCards}
            onCardExpand={handleCardExpand}
            expandedGroups={hookExpandedGroups} // Use hook's expanded groups state
            onGroupToggle={toggleGroupExpansion} // Use hook's toggle function
            groupingStrategy={groupingStrategy}
            actionLoading={actionLoading}
            confirmBroken={confirmBroken}
            confirmNotBroken={confirmNotBroken}
            clientId={currentUser?.customer_id}
            onDataRefresh={refetch}
            showToast={showToast}
          />
        </>
      )}

    </Box>
  );
};

export default AlertCardsView;